import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../../utils/content.util';
import { useNavigate, useParams } from 'react-router-dom';
import { DealerProspect } from '../../../models/dealer-prospect.model';
import { onError } from '../../../utils/error.util';
import {
  createDealerProspect,
  getDealerProspect,
  updateDealerProspect,
} from '../../../services/dealer-prospects.service';

import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import { Form, Formik } from 'formik';
import { DealerProspectSchema, dealerProspectInitialValues } from './validationSchema';
import { CreateDealerProspectRequest } from '../../../models/dealer-prospect.model';
import { isDev } from '../../../utils/utils';

export default function DealerProspectFormContainer() {
  const { isAuthenticated, currentUser } = useAppContext();
  const navigate = useNavigate();
  const { prospectId } = useParams();

  const [dealerProspect, setDealerProspect] = useState<DealerProspect>(dealerProspectInitialValues);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (prospectId && prospectId !== 'new') {
      loadDealerProspect();
    } else {
      // Set initial values for new lead
      const fullName = `${currentUser?.given_name ?? ''} ${currentUser?.family_name ?? ''}`.trim();
      setDealerProspect({
        ...dealerProspectInitialValues,
        teamMemberName: fullName,
        // division: currentUser?.['custom:division'] === 'CCL' ? Division.CCL : Division.TSF,
      });
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, prospectId, currentUser]);

  const loadDealerProspect = async () => {
    try {
      const response = await getDealerProspect(prospectId!);
      // Older prospects stored their note text in leadTypeName; fall back to it.
      setDealerProspect({ ...response, notes: response.notes || response.leadTypeName || '' });
    } catch (err) {
      onError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (values: CreateDealerProspectRequest) => {
    setIsSubmitting(true);
    try {
      if (prospectId && prospectId !== 'new') {
        await updateDealerProspect(prospectId, values);
      } else {
        await createDealerProspect(values);
      }
      navigate('/dealer-prospects');
    } catch (err) {
      onError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <CircularProgressComponent />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <h2>{prospectId === 'new' ? 'Add New Dealer Prospect' : 'Edit Dealer Prospect'}</h2>
      </Box>

      <Formik
        enableReinitialize
        initialValues={dealerProspect}
        validationSchema={DealerProspectSchema}
        onSubmit={onSubmit}
      >
        {({ handleChange, handleBlur, values, touched, errors, setFieldValue }) => (
          <Form noValidate>
            <Grid container spacing={3}>
              {/* Dealership */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Dealership"
                  name="dealershipName"
                  value={values.dealershipName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.dealershipName && Boolean(errors.dealershipName)}
                  helperText={touched.dealershipName && errors.dealershipName}
                />
              </Grid>

              {/* Notes */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="Notes"
                  name="notes"
                  value={values.notes ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.notes && Boolean(errors.notes)}
                  helperText={touched.notes && errors.notes}
                />
              </Grid>

              {/* Submit Buttons */}
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
                  <Button type="submit" variant="contained" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : prospectId === 'new' ? 'Create Prospect' : 'Update Prospect'}
                  </Button>
                  <Button variant="outlined" onClick={() => navigate('/dealer-prospects')} disabled={isSubmitting}>
                    Cancel
                  </Button>
                </Box>
              </Grid>

              {isDev ? (
                <>
                  <div>
                    <p>Initial Values:</p>
                    <code>{JSON.stringify(dealerProspect, null, 2)}</code>
                  </div>
                  <div>
                    <p>Errors:</p>
                    <code>{JSON.stringify(errors, null, 2)}</code>
                  </div>
                  <div>
                    <p>Current Values:</p>
                    <code>{JSON.stringify(values, null, 2)}</code>
                  </div>
                </>
              ) : null}
            </Grid>
          </Form>
        )}
      </Formik>
    </Container>
  );
}
